import {
  TimerNodeData,
  UpdateWorkflowNodeRequest,
  WorkflowNodeType,
  WorkflowsService,
} from '@/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useStore } from '@nanostores/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNodes } from '@xyflow/react';
import { Info } from 'lucide-react';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  useAutoSaveDebouncedCallback,
  useNodeConfigurationEventListener,
} from '../utils/nodeConfigurationUtils';

export default function TimerConfiguration({
  setNodes,
  selectedNode,
  readOnly = false,
  setAllRequiredFieldsOfNodeConfigurationFilled,
}: {
  setNodes: any;
  selectedNode: any;
  readOnly?: boolean;
  setAllRequiredFieldsOfNodeConfigurationFilled: Dispatch<
    SetStateAction<boolean>
  >;
}) {
  const nodes = useNodes();
  const wF = useStore($workflow);
  const queryClient = useQueryClient();

  const [delayAmount, setDelayAmount] = useState('');
  const [delayUnit, setDelayUnit] = useState('');

  function updateTimerConfig(value: string, type: 'amount' | 'unit') {
    if (readOnly) return;
    // get the current selected node
    const newNodes = JSON.parse(JSON.stringify(nodes));

    const updatableNode = newNodes.find((n: any) => n.id === selectedNode.id);

    // find the index of selected node
    const selectedNodeIndex = newNodes.findIndex(
      (n: any) => n.id === selectedNode.id,
    );

    if (updatableNode) {
      // update the node data
      if (type === 'amount') {
        updatableNode.data.delayAmount = parseInt(value);
      } else {
        updatableNode.data.delayUnit = value;
      }
      updatableNode.data.type = 'TIMER' as WorkflowNodeType;

      // Add this line to recalculate seconds
      updatableNode.data.delayInSeconds = calculateDelayInSeconds(
        updatableNode.data.delayAmount || 0,
        updatableNode.data.delayUnit || value
      );

      // set nodes
      newNodes[selectedNodeIndex] = updatableNode;

      setNodes(newNodes);
    }
  }
  function calculateDelayInSeconds(amount: number, unit: string): number {
    const conversions: { [key: string]: number } = {
      minutes: 60,
      hours: 3600,
      days: 86400,
      weeks: 604800,
      months: 2592000, // Assuming 30 days per month
    };

    return amount * (conversions[unit] || 0);
  }
  // mutation to update a node
  const updateNode = useMutation({
    mutationFn: async (payload: {
      req: UpdateWorkflowNodeRequest;
      nodeId: string;
    }) => {
      // call the api to create a new workspace
      if (wF) {
        const res = await WorkflowsService.updateWorkflowNode(
          wF.id,
          payload.nodeId,
          payload.req,
        );
        return res;
      }
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', wF?.id],
      });
      toast.success('Configurations updated!', {
        description: 'Your changes have been saved.',
        duration: 5000,
      });
    },
    onError: error => {
      console.error(error);

      // show a toast message
      toast.error('An error occurred :: Unable to add node.', {
        description: error.message,
        duration: 5000,
      });
    },
  });
  async function saveConfigurations() {
    const updatableNode = nodes.find((n: any) => n.id === selectedNode.id);

    if (!delayAmount) {
      toast.error('Please enter the delay amount!');
      return;
    }

    if (!delayUnit) {
      toast.error('Please enter the delay unit!');
      return;
    }

    if (updatableNode) {
      try {
        // console.log("updatableNode", updatableNode);
        await updateNode.mutateAsync({
          req: { data: updatableNode.data as TimerNodeData },
          nodeId: updatableNode?.id,
        });
      } catch (error) {
        console.error('Error saving node:', error);
      }
    }
  }

  useAutoSaveDebouncedCallback(
    async () => {
      await saveConfigurations();
    },
    2000, // 1 seconds delay
    [delayAmount, delayUnit] as const,
  );

  useNodeConfigurationEventListener('confirmSaveChanges', async detail => {
    await saveConfigurations();
  });

  function isTimerNodeData(data: any): data is TimerNodeData {
    return data.type === 'TIMER';
  }
  useEffect(() => {
    if (selectedNode) {
      if (isTimerNodeData(selectedNode.data)) {
        setDelayAmount(selectedNode?.data?.delayAmount || '');
        setDelayUnit(selectedNode?.data?.delayUnit || '');
      }
    }
  }, [selectedNode]);

  useEffect(() => {
    if (selectedNode && !readOnly && delayUnit && isTimerNodeData(selectedNode.data)) {
      // If local state has a value but node data doesn't match, update node data
      if (selectedNode.data.delayUnit !== delayUnit) {
        // console.log("Local state and node data mismatch, updating node with:", delayUnit);
        updateTimerConfig(delayUnit, 'unit');
      }
    }
  }, [delayUnit, selectedNode?.id]);

  return (
    <div className="h-full w-full">
      <p className=" text-sm font-semibold [&:not(:first-child)]:mt-6">
        Time Delay
      </p>
      <div className="mb-4 flex items-center">
        <Info className="mr-1 h-3 w-3 text-muted-foreground" />
        <p className="tracking-light text-xs text-muted-foreground">
          Wait for a specific amount of time before proceeding to the next node.
        </p>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <Input
          type="number"
          min="1"
          className="w-[150px] text-gray-500"
          placeholder="Enter amount"
          value={delayAmount}
          disabled={readOnly} // ✅ Disable in read-only mode
          onChange={e => {
            if (!readOnly) {
              // ✅ Prevent updates
              setDelayAmount(e.target.value);
              updateTimerConfig(e.target.value, 'amount');
            }
          }}
        />

        <Select
          value={delayUnit}
          disabled={readOnly} // ✅ Disable dropdown in read-only mode
          onValueChange={value => {
            if (!readOnly) {
              // ✅ Prevent updates
              setDelayUnit(value);
              updateTimerConfig(value, 'unit');
            }
          }}
        >
          <SelectTrigger
            className={`w-[150px] ${readOnly ? 'cursor-not-allowed bg-gray-200 text-gray-500' : ''}`}
          >
            <SelectValue placeholder="Select unit" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="minutes">Minutes</SelectItem>
              <SelectItem value="hours">Hours</SelectItem>
              <SelectItem value="days">Days</SelectItem>
              <SelectItem value="weeks">Weeks</SelectItem>
              <SelectItem value="months">Months</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <div className="mt-2 flex justify-end">
        <Button
          variant="outline"
          size="sm"
          disabled={readOnly || updateNode.isPending} // ✅ Disable in read-only mode
          className={
            readOnly ? 'cursor-not-allowed bg-gray-200 text-gray-500' : ''
          }
          onClick={saveConfigurations}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
